import ExperienceCard from '@/components/cards/experience-card/experience-card';
import {
  AttendeeStatus,
  ExperienceProgressStep,
  ExperienceStatus,
  useDeleteExperienceMutation,
  useExperienceSlotAttendeesLazyQuery,
  useGetExperienceAttendeesLazyQuery,
  useGetExperiencesByHostIdQuery,
} from '@/generated/graphql';
import { useCurrentUser } from '@/hooks/use-current-user/use-current-user';
import styled from '@emotion/styled';
import Delete from '@mui/icons-material/Delete';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PeopleIcon from '@mui/icons-material/People';
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  Stack,
} from '@mui/material';
import { CloseIconWhite } from '@/components/icons/icons';
import humanizeDuration from 'humanize-duration';
import { DateTime, Interval } from 'luxon';
import React, { Fragment, useEffect, useState } from 'react';
import CreateNewCard from './create-new-card';
import { useRouter } from 'next/navigation';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import { MenuItem, TextField as MuiTextField } from '@mui/material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
`;
const TypeTitle = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 136.8%;
  color: #ffffff;
`;
const CardsWrapper = styled.div`
  padding-top: 32px;
  display: flex;
  flex-wrap: wrap;
  columns: 3;
  column-gap: 32px;
  row-gap: 32px;
`;
const CardWrapper = styled.div`
  height: 323px;
  width: 288px;
  border-radius: 19px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

// Styled components for attendees dialog
const AttendeesHeaderContent = styled(Stack)`
  background-color: ${({ theme }) => theme.palette.common.homepagePurple};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const AttendeesMainContent = styled(Box)`
  background-color: ${({ theme }) => theme.palette.common.white};
  padding: ${({ theme }) => theme.spacing(2)};
  overflow-y: auto;
`;

interface ExperienceViewerProps {
  type:
    | ExperienceStatus.Draft
    | ExperienceStatus.Publish
    | ExperienceStatus.PendingReview
    | ExperienceStatus.Unlisted;
  title?: string;
}

type Experience = NonNullable<
  ReturnType<typeof useGetExperiencesByHostIdQuery>['data']
>['experiences']['edges'][0]['node'];

interface ExperienceCardWrapperProps {
  experience: Experience;
  i: number;
  refetch: ReturnType<typeof useGetExperiencesByHostIdQuery>['refetch'];
}

const ExperienceCardWrapper = ({ experience, i, refetch }: ExperienceCardWrapperProps) => {
  const router = useRouter();
  const [deleteExperience] = useDeleteExperienceMutation();
  // attendees dialog state
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [date, setDate] = useState<string>(DateTime.now().toISODate());
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [loadExperienceAttendees, experienceAttendeesResult] = useGetExperienceAttendeesLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
  const {
    data: attendeesData,
    loading: attendeesLoading,
    error: attendeesError,
  } = experienceAttendeesResult;

  const [loadExperienceSlotAttendees, experienceSlotAttendeesResult] =
    useExperienceSlotAttendeesLazyQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    });
  const { data: slotData, loading: slotLoading, error: slotError } = experienceSlotAttendeesResult;

  useEffect(() => {
    if (!attendeesOpen) {
      return;
    }
    setSelectedSlot('');
    void loadExperienceAttendees({ variables: { id: experience.id, date } });
    void loadExperienceSlotAttendees({ variables: { experienceId: experience.id, date } });
  }, [attendeesOpen, date, experience.id, loadExperienceAttendees, loadExperienceSlotAttendees]);

  useEffect(() => {
    const attendeesList = slotData?.experienceSlotAttendees ?? [];
    if (!attendeesList.length) {
      return;
    }
    const slotExists = attendeesList.some(slot => slot.slotId === selectedSlot);
    if (!slotExists) {
      setSelectedSlot(attendeesList[0].slotId);
    }
  }, [slotData, selectedSlot]);

  const getRedirectStep = (step: ExperienceProgressStep) => {
    switch (step) {
      case ExperienceProgressStep.Intro:
        return 'intro';

      case ExperienceProgressStep.Edit:
        return 'overview';

      case ExperienceProgressStep.Booking:
        return 'booking';

      case ExperienceProgressStep.Brainstorm:
        return 'brainstorm';

      case ExperienceProgressStep.Inspiration:
        return 'inspiration';

      case ExperienceProgressStep.ThreeThings:
        return 'three-things';

      case ExperienceProgressStep.Interests:
        return 'interests';

      case ExperienceProgressStep.PartOfDay:
        return 'partofday';

      case ExperienceProgressStep.Hobby:
        return 'hobbies';

      case ExperienceProgressStep.Final:
        return 'final';

      case ExperienceProgressStep.BrainstormIntro:
        return 'brainstorm-intro';

      default:
        return 'intro';
    }
  };

  const getLink = (experience: Experience) => {
    if (experience.status === ExperienceStatus.Publish) {
      return `/my-experiences/${experience?.id}/edit/overview`;
    }
    const expStep = experience.progressStep;
    const link =
      expStep === ExperienceProgressStep.Edit || expStep === ExperienceProgressStep.Brainstorm
        ? `/my-experiences/${experience.id}/edit/${getRedirectStep(expStep)}`
        : `/my-experiences/${experience.id}/prefill/${getRedirectStep(expStep || ExperienceProgressStep.Intro)}`;
    return link;
  };

  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClickActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setActionsAnchorEl(event.currentTarget);
  };

  const handleCloseActions = () => {
    setActionsAnchorEl(null);
  };

  const actionsOpen = Boolean(actionsAnchorEl);
  const actionsId = actionsOpen ? 'simple-popover' : undefined;

  const attendeeCount = attendeesData?.experienceAttendees?.users.length ?? 0;
  const attendeesHeadingPrefix = attendeesError
    ? 'Error loading'
    : attendeesLoading
      ? 'Loading'
      : attendeeCount === 0
        ? 'No'
        : attendeeCount;
  const slotAttendees = slotData?.experienceSlotAttendees ?? [];
  const selectedSlotEntry = slotAttendees.find(slot => slot.slotId === selectedSlot);
  const activeSlotAttendees = (selectedSlotEntry?.attendees ?? []).filter(
    attendee => attendee.status !== AttendeeStatus.Cancelled,
  );

  const intervalFromNow = Interval.fromDateTimes(
    DateTime.fromISO(experience.createdAt!),
    DateTime.now(),
  );
  const formattedIntervalFromNow = intervalFromNow.toDuration().valueOf();
  const humanizedFromNow = humanizeDuration(formattedIntervalFromNow, { round: true, largest: 1 });

  const title = experience?.title
    ? experience?.title
    : `untitled-${i + 1} (${humanizedFromNow} ago)`;
  return (
    <CardWrapper key={experience?.id}>
      <ExperienceCard
        experience={{
          ...experience,
          title,
        }}
        link={getLink(experience)}
        customActions={
          <div style={{ marginLeft: 'auto' }}>
            <IconButton aria-describedby={actionsId} onClick={handleClickActions}>
              <MoreHoriz />
            </IconButton>
            <Popover
              id={actionsId}
              open={actionsOpen}
              anchorEl={actionsAnchorEl}
              onClose={handleCloseActions}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <List>
                <ListItem
                  button
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCloseActions();
                    setAttendeesOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <PeopleIcon htmlColor="#555" />
                  </ListItemIcon>
                  <ListItemText>View Attendees</ListItemText>
                </ListItem>
                <ListItem
                  button
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!experience?.id) {
                      return null;
                    }
                    await deleteExperience({
                      variables: { ID: experience.id },
                    });
                    await refetch();
                    handleCloseActions();
                  }}
                >
                  <ListItemIcon>
                    <Delete htmlColor="#dd0c0c" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </ListItem>
              </List>
            </Popover>
            {/* Attendees Dialog */}
            <Dialog
              open={attendeesOpen}
              onClose={() => setAttendeesOpen(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: { backgroundColor: theme => theme.palette.common.midnight },
                onClick: (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation(),
              }}
              BackdropProps={{
                onClick: (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation(),
              }}
            >
              <AttendeesHeaderContent
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" color="white">
                  {experience.title}: {attendeesHeadingPrefix} attendees for {date}
                </Typography>
                <IconButton onClick={() => setAttendeesOpen(false)}>
                  <CloseIconWhite />
                </IconButton>
              </AttendeesHeaderContent>
              <AttendeesMainContent>
                <Box mb={2}>
                  <TextField
                    label="Select date"
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  {slotLoading ? (
                    <Typography>Loading slots...</Typography>
                  ) : slotError ? (
                    <Typography color="error">Error loading slots.</Typography>
                  ) : (
                    <MuiTextField
                      select
                      label="Select slot"
                      value={selectedSlot}
                      onChange={e => setSelectedSlot(e.target.value)}
                      fullWidth
                      margin="normal"
                    >
                      {slotAttendees.length > 0 ? (
                        slotAttendees.map((slot: any) => (
                          <MenuItem key={slot.slotId} value={slot.slotId}>
                            {new Date(slot.startTime).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            }) +
                              ' – ' +
                              new Date(slot.endTime).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" style={{ padding: '10px' }}>
                          No slots have been booked for this experience.
                        </MenuItem>
                      )}
                    </MuiTextField>
                  )}
                </Box>
                {attendeesLoading ? (
                  <Typography>Loading attendees...</Typography>
                ) : attendeesError ? (
                  <Typography color="error">Error loading attendees.</Typography>
                ) : (
                  <>
                    {!selectedSlotEntry ? null : (
                      <>
                        {activeSlotAttendees.length === 0 && (
                          <Typography>No attendees for this slot.</Typography>
                        )}
                        <List>
                          {activeSlotAttendees.map(attendee => {
                            const user = attendee.user;
                            return (
                              <ListItem key={attendee.id}>
                                <Avatar
                                  src={user?.avatar || ''}
                                  alt={user?.nickname || ''}
                                  sx={{ mr: 2 }}
                                />
                                <Typography>
                                  {user?.nickname ||
                                    `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                                </Typography>
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    )}
                  </>
                )}
              </AttendeesMainContent>
            </Dialog>
          </div>
        }
      />
    </CardWrapper>
  );
};

const ExperienceViewer = ({ type, title }: ExperienceViewerProps): React.ReactElement => {
  const { data: user } = useCurrentUser();
  const userId = user?.id;
  const { data: experiencesData, refetch } = useGetExperiencesByHostIdQuery({
    variables: {
      filter: {
        hostId: { eq: userId },
        status: { eq: type },
      },
    },
    fetchPolicy: 'network-only',
  });

  const experiences = (experiencesData?.experiences?.edges.map(edge => edge.node) || []).sort(
    (a, b) => (a.createdAt! < b.createdAt! ? -1 : 1),
  );

  return (
    <Container>
      <TypeTitle>{title}</TypeTitle>
      <CardsWrapper>
        {type === ExperienceStatus.Publish && <CreateNewCard />}
        {experiences.map((experience, i) => {
          return (
            <ExperienceCardWrapper
              experience={experience}
              i={i}
              key={experience.id}
              refetch={refetch}
            />
          );
        })}
      </CardsWrapper>
    </Container>
  );
};

export default ExperienceViewer;
